import {
  Button,
  Checkbox,
  Form,
  FormField,
  FormItem,
  FormLabel,
  Input,
  Label,
  ModalContent,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useErrorHandler,
} from '@sandtech-hub-monorepo/ui-kit';
import { ReactComponent as UniversityIcon } from '../../assets/Icons/university-icon.svg';
import React from 'react';
import { BulbIcon } from '@sandtech-hub-monorepo/ui-kit-site';
import { useIsSet } from '../../../utils';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import {
  CreateProgramApplicationDocument,
  FeePaymentMode,
  GetViewerLearningDetailsDocument,
  TuitionFeePaymentType,
} from '@sandtech-hub-monorepo/ehub-client';
import { useNavigate } from 'react-router-dom';
import { Loader2Icon } from 'lucide-react';

const feePaymentModes = {
  'I will pay the fee myself': FeePaymentMode.SelfPaying,
  'I will receive financial support from family or friends':
    FeePaymentMode.FamilySupport,
  'I have secured an external sponsor who will cover the cost':
    FeePaymentMode.Sponsorship,
  'I have secured an external scholarship or bursary that will cover the cost':
    FeePaymentMode.Scholarship,
  'Other (please specify)': FeePaymentMode.Other,
};

const tuitionFeePaymentType = {
  'Yes, bank transfer': TuitionFeePaymentType.BankTransfer,
  'Yes, debit card': TuitionFeePaymentType.DebitCard,
  'Yes, either': TuitionFeePaymentType.Either,
  No: TuitionFeePaymentType.No,
};

const enrollInfoSchema = z.object({
  feePaymentMode: z.nativeEnum(FeePaymentMode),
  tuitionFeePaymentType: z.nativeEnum(TuitionFeePaymentType),
  otherFeePaymentMode: z.string().optional(),
  anticipateChallenges: z.boolean(),
  additionalInfo: z.string().optional(),
  sharePersonalDataConsent: z.boolean(),
});

const TOTAL_TUITION_COST = 1575;

interface Props {
  fee?: string | null;
  name: string;
  programId: string;
  close: () => void;
}

export const EnrollInfoModal = ({ fee, name, programId, close }: Props) => {
  const { handleApolloError } = useErrorHandler();
  const navigate = useNavigate();
  const isSet = useIsSet();

  const numericFee = fee ? parseFloat(fee.replace(/[^0-9.]/g, '')) : undefined;
  const durationInSemesters =
    numericFee && numericFee > 0
      ? Math.round(TOTAL_TUITION_COST / numericFee)
      : undefined;

  const form = useForm<z.infer<typeof enrollInfoSchema>>({
    defaultValues: {
      otherFeePaymentMode: '',
      additionalInfo: '',
    },
  });

  const [createApplication, { loading: creatingApplication }] = useMutation(
    CreateProgramApplicationDocument,
    {
      onError: handleApolloError,
      refetchQueries: [
        {
          query: GetViewerLearningDetailsDocument,
        },
      ],
      onCompleted: () => {
        navigate('/');
        close();
      },
    }
  );

  const submit = async () => {
    const {
      feePaymentMode,
      anticipateChallenges,
      otherFeePaymentMode,
      additionalInfo,
      tuitionFeePaymentType,
      sharePersonalDataConsent,
    } = form.getValues();

    createApplication({
      variables: {
        input: {
          programId,
          feePaymentMode,
          anticipateChallenges,
          ...(feePaymentMode === FeePaymentMode.Other
            ? { otherFeePaymentMode }
            : {}),
          additionalInfo,
          tuitionFeePaymentType,
          sharePersonalDataConsent,
        },
      },
    });
  };

  return (
    <ModalContent>
      {isSet ? (
        <div className="flex flex-col items-center text-center gap-6 max-w-screen-xs w-full mx-auto">
          <h2 className="font-semibold text-2xl">
            Tuition Payment Information
            <br />
            <span className="text-primary">Questionnaire</span>
          </h2>
          <div className="flex gap-6 w-full">
            <div className="flex-1 border border-border rounded-2xl p-4 flex flex-col items-center justify-start gap-1 text-left">
              <div className="text-foreground">Total cost</div>
              <div className="text-2xl font-semibold text-foreground">
                ${TOTAL_TUITION_COST.toLocaleString()}
              </div>
              <div className="text-muted-foreground">{fee}</div>
            </div>
            <div className="flex-1 border border-border rounded-2xl p-4 flex flex-col items-center justify-start gap-1 text-left">
              <div className="text-foreground">Duration</div>
              <div className="text-2xl font-semibold text-foreground">
                {durationInSemesters ? `${durationInSemesters} semesters` : '-'}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center text-center gap-2 max-w-screen-xs mx-auto">
          <div
            className={`p-6 bg-primary/20 rounded-full text-primary mx-auto mb-2`}
          >
            <UniversityIcon height="48" width="48" />
          </div>
          <h2 className={`text-primary font-semibold sm:text-2xl text-xl`}>
            Tuition Payment Information Questionnaire
          </h2>
          <div>
            Please note that the total cost to complete the <b>{name}</b>{' '}
            program will cost you:
          </div>
          <div className="sm:text-2xl text-xl text-primary font-semibold">
            {fee}
          </div>
        </div>
      )}
      <div className="flex flex-col gap-6 max-w-screen-xs mx-auto">
        <div
          className={
            isSet
              ? 'bg-background-quaternary p-2.5 rounded-md flex gap-3 items-center mt-8'
              : 'bg-banner p-2.5 rounded-md flex gap-3 items-center mt-8'
          }
        >
          <div
            className={
              isSet
                ? 'p-3 rounded-full bg-card text-primary'
                : 'p-3 rounded-full bg-background text-primary'
            }
          >
            <BulbIcon />
          </div>
          <div
            className={isSet ? 'text-foreground' : 'text-primary-foreground'}
          >
            To better understand how you plan to fund your degree, please answer
            the below questions.
          </div>
        </div>
        <Form {...form}>
          <div className="flex flex-col gap-6">
            <FormField
              control={form.control}
              name="feePaymentMode"
              render={({ field }) => (
                <div className="flex flex-col gap-2">
                  <FormLabel className="leading-5">
                    How do you plan to pay the tuition fee of $
                    {TOTAL_TUITION_COST.toLocaleString()} if you’re admitted at
                    FIT (Frontier Institute of Technology)?
                  </FormLabel>
                  <Select
                    {...field}
                    value={field.value || ''}
                    onValueChange={(v) => {
                      field.onChange(v);
                      form.setValue('otherFeePaymentMode', '');
                    }}
                    defaultValue="Please select"
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Please select" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(feePaymentModes).map(([key, value]) => (
                        <SelectItem value={value} key={key}>
                          {key}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            />
            {form.watch('feePaymentMode') === FeePaymentMode.Other && (
              <FormField
                control={form.control}
                name="otherFeePaymentMode"
                render={({ field }) => (
                  <div className="flex flex-col gap-2">
                    <FormLabel>Please specify</FormLabel>
                    <Input {...field} placeholder="Type here" />
                  </div>
                )}
              />
            )}
            <FormField
              control={form.control}
              name="tuitionFeePaymentType"
              render={({ field }) => (
                <div className="flex flex-col gap-2">
                  <FormLabel className="leading-5">
                    Do you have the ability to make tuition payments by bank
                    transfer or debit card?
                  </FormLabel>
                  <Select
                    {...field}
                    value={field.value || ''}
                    onValueChange={field.onChange}
                    defaultValue="Please select"
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Please select" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(tuitionFeePaymentType).map(
                        ([key, value]) => (
                          <SelectItem value={value} key={key}>
                            {key}
                          </SelectItem>
                        )
                      )}
                    </SelectContent>
                  </Select>
                </div>
              )}
            />
            <FormField
              control={form.control}
              name="anticipateChallenges"
              render={({ field }) => (
                <div className="flex gap-2 flex-col">
                  <FormLabel>
                    Do you anticipate any challenges in covering the tuition
                    fee?
                  </FormLabel>
                  <FormItem className="flex w-full items-center gap-2 space-y-0">
                    <Checkbox
                      checked={form.getValues('anticipateChallenges') === true}
                      onCheckedChange={() =>
                        form.setValue('anticipateChallenges', true)
                      }
                    />
                    <FormLabel>Yes</FormLabel>
                  </FormItem>
                  <FormItem className="flex w-full items-center gap-2 space-y-0">
                    <Checkbox
                      checked={form.getValues('anticipateChallenges') === false}
                      onCheckedChange={() =>
                        form.setValue('anticipateChallenges', false)
                      }
                    />
                    <FormLabel>No</FormLabel>
                  </FormItem>
                </div>
              )}
            />
            <FormField
              render={({ field }) => (
                <div className="flex flex-col gap-2">
                  <div className="text-sm mb-1">
                    Optional: If there is additional information you’d like to
                    share about your financial situation please provide it here:
                  </div>
                  <Input {...field} placeholder="Type here" />
                </div>
              )}
              name="additionalInfo"
            />
            <FormField
              control={form.control}
              name="sharePersonalDataConsent"
              render={({ field }) => (
                <FormItem className="flex w-full items-start gap-2 space-y-0">
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                  <FormLabel className="leading-5">
                    By submitting this application, I give permission for ALX to
                    share my personal data with Frontier Institute of
                    Technology.
                  </FormLabel>
                </FormItem>
              )}
            />
          </div>
        </Form>
      </div>
      <Button
        className="mx-auto my-8"
        onClick={submit}
        disabled={
          creatingApplication ||
          !form.watch('feePaymentMode') ||
          form.watch('anticipateChallenges') == undefined ||
          (form.watch('feePaymentMode') === FeePaymentMode.Other &&
            !form.watch('otherFeePaymentMode')) ||
          !form.watch('sharePersonalDataConsent') ||
          !form.watch('tuitionFeePaymentType')
        }
      >
        {creatingApplication ? (
          <>
            <Loader2Icon className="animate-spin" /> Submitting...
          </>
        ) : (
          'Submit my answers'
        )}
      </Button>
    </ModalContent>
  );
};
